import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import routes from '../../utils/routes';
import {
  FiGrid, FiUsers, FiDollarSign, FiCalendar, FiStar,
  FiMail, FiShield, FiMessageSquare, FiFileText, FiArrowLeft,
  FiMenu, FiX, FiChevronRight, FiDatabase, FiEdit, FiCpu, FiLayers,
} from 'react-icons/fi';

interface AdminNavItem {
  name: string;
  icon: any;
  route: string;
}

const adminNavItems: AdminNavItem[] = [
  { name: 'Dashboard', icon: FiGrid, route: routes.adminDashboard },
  { name: 'Users', icon: FiUsers, route: routes.adminUsers },
  { name: 'Payments', icon: FiDollarSign, route: routes.adminPayments },
  { name: 'Bookings', icon: FiCalendar, route: routes.adminBookings },
  { name: 'Experts', icon: FiStar, route: routes.adminExperts },
  { name: 'Buckets', icon: FiDatabase, route: routes.adminBuckets },
  { name: 'Companies', icon: FiEdit, route: routes.adminCompanyInsights },
  { name: 'Email', icon: FiMail, route: routes.adminEmail },
  { name: 'Blacklist', icon: FiShield, route: routes.adminBlacklist },
  { name: 'Support', icon: FiMessageSquare, route: routes.adminSupport },
  { name: 'Audit Log', icon: FiFileText, route: routes.adminAuditLog },
  { name: 'LLM Config', icon: FiCpu, route: routes.adminLLMConfig },
  { name: 'Embeddings', icon: FiLayers, route: routes.adminEmbeddingConfig },
];

const AdminLayout: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);
  const isAdmin = useAppSelector((state) => state.user.isAdmin);
  const isAuthenticated = useAppSelector((state) => state.user.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const hasAdminAccess = isAdmin || user?.is_admin === true;

  useEffect(() => {
    // Only redirect if we're sure the user is loaded and not admin
    if (isAuthenticated && user && !hasAdminAccess) {
      navigate(routes.home, { replace: true });
    } else if (!isAuthenticated) {
      navigate(routes.login, { replace: true });
    }
  }, [isAuthenticated, user, hasAdminAccess, navigate]);

  if (!hasAdminAccess) return null;

  const isActive = (route: string) => {
    if (route === routes.adminDashboard) {
      return location.pathname === route;
    }
    return location.pathname.startsWith(route);
  };

  const sidebar = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-5 border-b flex items-center gap-3" style={{ borderColor: colors.borderColor }}>
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${colors.primary}20` }}
        >
          {React.createElement(FiShield as any, { size: 18, color: colors.primary })}
        </div>
        <div>
          <h2 className="text-sm font-bold" style={{ color: colors.textColor }}>Admin Panel</h2>
          <p className="text-xs" style={{ color: colors.grayScale500 }}>{user?.name}</p>
        </div>
        {mobileMenuOpen && (
          <button onClick={() => setMobileMenuOpen(false)} className="ml-auto p-1.5 rounded-lg" style={{ backgroundColor: colors.inputBackground }}>
            {React.createElement(FiX as any, { size: 18, color: colors.textColor })}
          </button>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-3">
        {adminNavItems.map((item) => {
          const active = isActive(item.route);
          return (
            <button
              key={item.route}
              onClick={() => { navigate(item.route); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 mx-2 text-left transition-all duration-200 rounded-xl"
              style={{
                color: active ? colors.primary : colors.textColor,
                backgroundColor: active ? `${colors.primary}15` : 'transparent',
                width: 'calc(100% - 16px)',
              }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = colors.hoverBackground || `${colors.primary}08`; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {React.createElement(item.icon as any, { size: 18, color: active ? colors.primary : colors.grayScale500 })}
              <span className="text-sm" style={{ fontWeight: active ? 600 : 400 }}>{item.name}</span>
              {active && (
                <div className="ml-auto">
                  {React.createElement(FiChevronRight as any, { size: 14, color: colors.primary })}
                </div>
              )}
            </button>
          );
        })}
      </nav>

      {/* Back to app */}
      <div className="p-4 border-t" style={{ borderColor: colors.borderColor }}>
        <button
          onClick={() => navigate(routes.home)}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors"
          style={{ backgroundColor: colors.inputBackground }}
        >
          {React.createElement(FiArrowLeft as any, { size: 18, color: colors.grayScale500 })}
          <span className="text-sm" style={{ color: colors.grayScale500 }}>Back to App</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: colors.backgroundColor, color: colors.textColor }}>
      {/* Mobile Header */}
      <header
        className="lg:hidden fixed top-0 left-0 right-0 h-14 flex items-center px-4 z-40 border-b"
        style={{
          backgroundColor: colors.glassBackground || colors.backgroundColor,
          borderColor: colors.glassBorder || colors.borderColor,
          backdropFilter: 'blur(12px)',
        }}
      >
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="p-2 rounded-xl"
          style={{ backgroundColor: colors.inputBackground }}
        >
          {React.createElement(FiMenu as any, { size: 22, color: colors.textColor })}
        </button>
        <div className="flex items-center gap-2 ml-3">
          {React.createElement(FiShield as any, { size: 18, color: colors.primary })}
          <span className="font-semibold text-sm" style={{ color: colors.textColor }}>Admin Panel</span>
        </div>
      </header>

      {/* Mobile Sidebar Drawer */}
      {mobileMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
          <aside
            className="fixed left-0 top-0 h-full w-72 z-[60] flex flex-col border-r overflow-hidden"
            style={{
              backgroundColor: colors.glassBackground || colors.backgroundColor,
              borderColor: colors.glassBorder || colors.borderColor,
              backdropFilter: 'blur(12px)',
            }}
          >
            {sidebar}
          </aside>
        </>
      )}

      {/* Desktop Sidebar */}
      <aside
        className="hidden lg:flex flex-col fixed left-0 top-0 h-full w-64 border-r z-50 overflow-hidden"
        style={{
          backgroundColor: colors.glassBackground || colors.backgroundColor,
          borderColor: colors.glassBorder || colors.borderColor,
          backdropFilter: 'blur(12px)',
        }}
      >
        {sidebar}
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0 overflow-y-auto min-h-screen">
        <div className="w-full h-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
